import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import { useTranslation } from "react-i18next";
import { DARKCOLORS, LIGHTCOLORS, ROUTES } from "../../../Utils/constants";
import ArrowButton from "../../../Components/Common/ArrowButton";
import { useThemeContext } from "../../../Context/ThemeContext";
import { getMachineBasicDetails } from "../../../Services/authService";
import { useNavigate } from "react-router-dom";
import SectionLoader from "../../../Components/Common/SectionLoader";
import "./MachineStatus.css";

function MachineStatus() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { mode } = useThemeContext();

  const [machineData, setMachineData] = useState([]);
  const [loading, setLoading] = useState(false);

 const isReturnRow = (row) => {
  const typeName = (
    row.machineType ||
    row.type ||
    row.vendingMachineType ||
    ""
  ).toLowerCase();

  // If the backend sends a type name, trust it
  if (typeName) {
    return (
      typeName.includes("return") ||
      typeName.includes("recycle") ||
      typeName.includes("greeny")
    );
  }

  // Fallback only when no type name is available
  return row.bowlBinLevel != null || row.bottleBinLevel != null;
};

  // True only when every machine in the list is a return machine
const hideTemperature =
  machineData.length > 0 && machineData.every(isReturnRow);
  
  const colorDanger =
    mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;
  const colorDanger1 =
    mode === "dark" ? DARKCOLORS.Danger100 : LIGHTCOLORS.Danger100;
  const colorDanger3 =
    mode === "dark" ? DARKCOLORS.Danger600 : LIGHTCOLORS.Danger600;
  const colorWarning =
    mode === "dark" ? DARKCOLORS.Warning500 : LIGHTCOLORS.Warning500;
  const colorWarning1 =
    mode === "dark" ? DARKCOLORS.Warning100 : LIGHTCOLORS.Warning100;
  const colorWarning3 =
    mode === "dark" ? DARKCOLORS.Warning300 : LIGHTCOLORS.Warning300;
  const colorWarning2 =
    mode === "dark" ? DARKCOLORS.Warning700 : LIGHTCOLORS.Warning700;
  const colorSuccess =
    mode === "dark" ? DARKCOLORS.Success700 : LIGHTCOLORS.Success700;
  const colorSuccess1 =
    mode === "dark" ? DARKCOLORS.Success100 : LIGHTCOLORS.Success100;
  const colorSuccess3 =
    mode === "dark" ? DARKCOLORS.Success500 : LIGHTCOLORS.Success500;
  const containerBackground2 =
    mode === "light" ? LIGHTCOLORS.GreyScale10 : DARKCOLORS.GreyScale10;
  const btnColor =
    mode === "light" ? LIGHTCOLORS.GreyScale80 : DARKCOLORS.GreyScale80;
  const btnTextColor =
    mode === "light"
      ? LIGHTCOLORS.HeadingDarkBackground
      : DARKCOLORS.HeadingDarkBackground;
  const subTextColor =
    mode === "light"
      ? LIGHTCOLORS.BodyLightBackground
      : DARKCOLORS.BodyLightBackground;

  // Greyscale badge for missing (null) backend data
  const greyBadgeBg =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const greyBadgeBorder =
    mode === "dark" ? DARKCOLORS.GreyScale60 : LIGHTCOLORS.GreyScale60;
  const greyBadgeText =
    mode === "dark"
      ? DARKCOLORS.HeadingLightBackground
      : LIGHTCOLORS.HeadingLightBackground;

  useEffect(() => {
    const fetchMachinesData = async () => {
      try {
        setLoading(true);
        const response = await getMachineBasicDetails();
        // console.log("MACHINE ROW:", response.data[0]);

        // Sorting based on condition and status priority
        const sortedData = response.data.sort((a, b) => {
          const priority = {
            "Abnormal-Inactive": 1,
            "Abnormal-Active": 2,
            "Normal-Inactive": 3,
            "Normal-Active": 4,
          };

          // Create key for sorting
          const keyA = `${a.condition}-${a.status}`;
          const keyB = `${b.condition}-${b.status}`;

          return (priority[keyA] || 5) - (priority[keyB] || 5); // Default priority is 5 if not listed
        });
        setMachineData(sortedData);
      } catch (error) {
        console.error(error.message || t("dashboard.errorFetchingMachines"));
      } finally {
        setLoading(false);
      }
    };
    fetchMachinesData();
  }, []);

  // Icon map for temperature
  // const tempIcon = (type) => {
  //   switch (type) {
  //     case "cold":
  //       return "❄️"; // cold icon
  //     case "hot":
  //       return "🔥"; // hot icon
  //     default:
  //       return "❄️"; // default cold
  //   }
  // };

  //Status badge styles
  const getStatusBadgeStyles = (status) => {
    if (!status) {
      return {
        backgroundColor: greyBadgeBg,
        border: `1px solid ${greyBadgeBorder}`,
        //dotColor: greyBadgeText,
        textstyle: greyBadgeText,
      };
    }
    switch (status) {
      case "Active":
        return {
          backgroundColor: colorSuccess1,
          border: `1px solid ${colorSuccess3}`,
          //dotColor: colorSuccess,
          textstyle: colorSuccess,
        };
      case "Inactive":
        return {
          backgroundColor: colorDanger1,
          border: `1px solid ${colorDanger}`,
          textstyle: colorDanger3,
        };
      default:
        return {
          backgroundColor: colorWarning1,
          border: `1px solid ${colorWarning3}`,
          // dotColor: colorWarning,
          textstyle: colorWarning2,
        };
    }
  };

  const scroll1 =
    mode === "light"
      ? LIGHTCOLORS.BodyDarkBackground
      : DARKCOLORS.BodyLightBackground;

  const scroll =
    mode === "light" ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;
  // Condition badge styles
  const getConditionBadgeStyles = (condition) => {
    if (!condition) {
      return {
        backgroundColor: greyBadgeBg,
        border: `1px solid ${greyBadgeBorder}`,
        color: greyBadgeText,
      };
    }
    const isNormal = condition === "Normal";
    return {
      backgroundColor: isNormal ? colorSuccess1 : colorDanger1,
      border: `1px solid ${isNormal ? colorSuccess : colorDanger}`,
      color: isNormal ? colorSuccess : colorDanger,
    };
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Header */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <h3 className="subheading-sec-card" style={{ margin: "0px" }}>
          {t("dashboard.machineStatus")}
        </h3>
        <ArrowButton targetRoute={ROUTES.VENDING_MACHINES} />
      </Grid>

      {/* Table */}
      <Box
        sx={{
          width: "100%", // ...(mode === "dark" && {
          maxHeight: "300px ",
          overflowX: "auto",
          overflowY: "auto",
          paddingRight: "5px",
          // Horizontal & vertical scrollbar: 8px track, 1px border thumb = 6px visible, centered
          "&::-webkit-scrollbar": {
            width: "10px !important",
            height: "8px !important",
            padding: "2px !important",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: scroll1,
            borderRadius: "10px",
            border: `1px solid ${scroll}`,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: scroll,
            borderRadius: "10px",
            width: "3px !important",

            border: "3px solid transparent",
            backgroundClip: "padding-box",
            "&:hover": {
              backgroundColor: scroll,
            },
          },
          // }),
        }}
      >
        <TableContainer>
          <Table
            stickyHeader
            sx={{
              width: "100%",
              tableLayout: { md: "fixed" },
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <TableHead sx={{ position: "sticky", top: 0, zIndex: 1 }}>
              <TableRow>
                <TableCell
                  sx={{ minWidth: 150, backgroundColor: "transparent" }}
                >
                  {t("dashboard.location")}
                </TableCell>
                <TableCell
                  sx={{ minWidth: 100, backgroundColor: "transparent" }}
                >
                  {t("dashboard.number")}
                </TableCell>
                {!hideTemperature && (
  <TableCell
    sx={{ minWidth: 120, backgroundColor: "transparent" }}
  >
    {t("dashboard.temperature")}
  </TableCell>
)}
                <TableCell
                  sx={{ minWidth: 100, backgroundColor: "transparent" }}
                >
                  {t("dashboard.status")}
                </TableCell>
                <TableCell
                  sx={{ minWidth: 100, backgroundColor: "transparent" }}
                >
                  {t("dashboard.condition")}
                </TableCell>
                <TableCell
                  sx={{ minWidth: 100, backgroundColor: "transparent" }}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{}}>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ border: "none", color: subTextColor }}
                  >
                    <SectionLoader />
                  </TableCell>
                </TableRow>
              ) : machineData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ border: "none", color: subTextColor }}
                  >
                    {t("dashboard.inventoryLevelError")}
                  </TableCell>
                </TableRow>
              ) : (
                machineData.map((row, index) => {
                  const statusStyles = getStatusBadgeStyles(row.status);
                  const conditionStyles = getConditionBadgeStyles(
                    row.condition,
                  );
                  return (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor:
                          index % 2 === 0
                            ? "transparent"
                            : containerBackground2,
                      }}
                    >
                      <TableCell
                        sx={{
                          border: "none",
                          maxWidth: 160,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          ...(index % 2 !== 0 && {
                            borderRadius: "12px 0 0 12px",
                          }),
                        }}
                      >
                        <Tooltip title={row.location} arrow placement="top">
                          <Box
                            sx={{
                              color: subTextColor,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "inline-flex",
                              alignItems: "center",
                              maxWidth: "100%",
                              position: "relative",
                              cursor: "default",
                            }}
                            className="label-tex"
                          >
                            {row.location}
                            {row.condition === "Abnormal" && (
                              <CircleIcon
                                sx={{
                                  fontSize: "9px",
                                  color: colorDanger,
                                  position: "absolute",
                                  top: 0,
                                  right: -12,
                                }}
                              />
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>

                      <TableCell
                        className="label-tex"
                        sx={{
                          border: "none",
                          color: subTextColor,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.machineNumber}
                      </TableCell>
                                            {!hideTemperature && (
                        <TableCell
                          className="label-tex"
                          sx={{
                            border: "none",
                            color: subTextColor,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {isReturnRow(row)
                            ? ""
                            : row.temperature != null && row.temperature !== ""
                              ? `${row.temperature}°C`
                              : "-"}
                        </TableCell>
                      )}
                      <TableCell className="label-tex" sx={{ border: "none" }}>
                        {/* Status Badge */}
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "6px 10px",
                            borderRadius: "99px",
                            backgroundColor: statusStyles.backgroundColor,
                            border: statusStyles.border,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 500,
                              color: statusStyles.textstyle,
                              fontFamily: "Satoshi-medium",
                            }}
                          >
                            {row.status === "Active"
                              ? t("vendingMachines.active", "Active")
                              : row.status === "Inactive"
                                ? t("vendingMachines.inactive", "Inactive")
                                : row.status || t("sales_analytics.NoData")}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell className="label-tex" sx={{ border: "none" }}>
                        {/* Condition Badge */}
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "6px 10px",
                            borderRadius: "99px",
                            backgroundColor: conditionStyles.backgroundColor,
                            border: conditionStyles.border,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 500,
                              color: conditionStyles.color,
                              fontFamily: "Satoshi-medium",
                            }}
                          >
                            {row.condition == null || row.condition === ""
                              ? t("sales_analytics.NoData")
                              : row.condition === "Normal"
                                ? t("vendingMachines.normal", "Normal")
                                : t("vendingMachines.abnormal", "Abnormal")}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        className="label-tex"
                        sx={{
                          border: "none",
                          color: subTextColor,
                          ...(index % 2 !== 0 && {
                            borderRadius: "0 12px 12px 0",
                          }),
                        }}
                      >
                        <Button
                          variant={
                            row.condition === "Abnormal"
                              ? "contained"
                              : "outlined"
                          }
                          size="small"
                          sx={{
                            backgroundColor:
                              row.condition === "Abnormal"
                                ? btnColor
                                : "transparent",
                            color:
                              row.condition === "Abnormal"
                                ? btnTextColor
                                : btnColor,
                            borderColor:
                              row.condition === "Abnormal" ? "none" : btnColor,
                            borderRadius: "50px",
                            padding: "6px  12px  3px 12px",
                            textTransform: "none",
                          }}
                          onClick={() =>
                            navigate(`${ROUTES.MACHINE_DETAILS}/${row.id}`)
                          }
                        >
                          {t("dashboard.view")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

export default MachineStatus;
